import { ACCESS_TOKEN_KEY, ACCESS_TOKEN_MAX_AGE, HTTP_OK, HTTP_OK_2 } from '@/utils/constant';
import { setCookie } from '@/utils/cookies';
import { AxiosResponse, isAxiosError } from 'axios';
import _isEqual from 'lodash/isEqual';
import { ApiErrorResponse, CommonApiError } from '../types/common';

export const emptyFunction = () => {};

export const noOptionsMessage = () => 'No results found';

// Generic response handler for api
export const handleResponse = async <T>(
  response: AxiosResponse<{
    data: T;
    accessToken?: string;
    message: string;
    status: boolean;
    pagination?: {
      totalRecords: number;
      currentPage: number;
      totalPages: number;
      pageSize: number;
      isNextPage: boolean;
    };
  }>,
): Promise<{
  data: T;
  accessToken?: string;
  message: string;
  status: boolean;
  pagination?: {
    totalRecords: number;
    currentPage: number;
    totalPages: number;
    pageSize: number;
    isNextPage: boolean;
  };
}> => {
  // Check for error status
  if (![HTTP_OK, HTTP_OK_2].includes(response.status)) {
    return {
      message: response.data?.message ?? 'Unknown error occurred',
      status: false,
      data: response.data?.data as T,
      pagination: response.data.pagination,
    };
  }

  // Handle successful response with token
  if (response.data?.accessToken) {
    await setCookie(ACCESS_TOKEN_KEY, response.data.accessToken, {
      maxAge: ACCESS_TOKEN_MAX_AGE,
      path: '/',
    });
  }
  return response.data;
};

export const handleErrorResponse = <T = null>(error: CommonApiError): ApiErrorResponse<T> => {
  const errorData = error.response?.data;
  const errors = error?.response?.data?.errors;

  return {
    message: errorData?.message ?? 'Unknown error occurred',
    status: false,
    data: (errorData?.data ?? null) as T,
    accessToken: null,
    pagination: undefined,
    errors,
  };
};
export const displayErrorMessage = (error: unknown): string => {
  if (isAxiosError<{ message: string | string[] }>(error)) {
    const msg = error?.response?.data?.message || 'An unknown error occurred';
    if (typeof msg === 'string') {
      return msg;
    } else if (Array.isArray(msg)) {
      return msg.join('\n');
    }
  }

  return (error as Error)?.message || 'An unknown error occurred';
};

export function stripNonnumericExceptDecimal(inputStr: string | null | undefined): number {
  let tempStr = '';

  // Handle null/empty
  if (inputStr == null || inputStr.trim() === '') {
    return 0;
  }

  for (let i = 0; i < inputStr.length; i++) {
    const char = inputStr.charAt(i);

    if (i === 0) {
      // Allow digit or minus sign for first character
      if (!isNaN(Number(char)) || char === '-') {
        tempStr += char;
      }
    } else {
      // Allow digits and decimal point for other characters
      const isDigitOrDecimal = !isNaN(Number(char)) || char === '.';
      if (isDigitOrDecimal) {
        tempStr += char;
      }
    }
  }

  return Number(tempStr);
}

/**
 * Compares two objects (deeply) while excluding a specified list of keys.
 * T extends object allows for loose typing at the call site.
 */
export const hasActualChanges = <T extends object>(
  obj1: T | null | undefined,
  obj2: T | null | undefined,
  excludeKeys: string[] = [],
): boolean => {
  if (!obj1 || !obj2) {
    return false;
  }

  const omitKeys = (obj: T, keys: string[]): Partial<T> => {
    // ... (rest of the omitKeys logic remains the same)
    const newObj: Partial<T> = {};
    const excludeSet = new Set(keys);

    for (const key of Object.keys(obj) as Array<keyof T>) {
      if (Object.hasOwn(obj, key as string) && !excludeSet.has(key as string)) {
        newObj[key] = obj[key];
      }
    }
    return newObj;
  };

  const filteredObj1 = omitKeys(obj1, excludeKeys);
  const filteredObj2 = omitKeys(obj2, excludeKeys);

  return !_isEqual(filteredObj1, filteredObj2);
};

// Helper function to safely convert values to strings
export const safeStringifyValue = (val: unknown): string => {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  if (typeof val === 'boolean') return String(val);
  if (typeof val === 'object') {
    try {
      return JSON.stringify(val);
    } catch {
      return ' ';
    }
  }
  return '';
};

export const getErrorMessage = (err: unknown, optionalMessage: string): string => {
  const apiErr = err as { errors?: unknown; message?: unknown };

  // Case: errors is an array
  if (Array.isArray(apiErr.errors) && apiErr.errors.length) {
    return apiErr.errors.join(', ');
  }

  // Case: message is an array
  if (Array.isArray(apiErr.message) && apiErr.message.length) {
    return apiErr.message.join(', ');
  }

  // Case: message is a string
  if (typeof apiErr.message === 'string' && apiErr.message.trim()) {
    return apiErr.message;
  }

  return optionalMessage;
};

export const shortID = (length = 8) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => (b % 36).toString(36)).join('');
};
